import { Observable } from 'rxjs';

/**
 * Configuration d'une étape de progression
 */
export interface ProgressStep {
    /** Identifiant unique de l'étape */
    id: string;

    /** Libellé affiché à l'utilisateur */
    label: string;

    /** Poids relatif pour le calcul du % global (ex: 15, 60, 25) */
    weight: number;

    /** Durée estimée en ms (pour l'animation fake) */
    estimatedDurationMs?: number;

    /** Si true, l'étape peut reporter un progress réel (ex: page X/Y) */
    hasRealProgress?: boolean;

    /** Messages affichés pendant l'animation fake, selon le temps passé dans l'étape (rassurer sur une attente longue) */
    waitingMessages?: ProgressWaitingMessage[];
}

/**
 * Message d'attente affiché à partir d'un délai écoulé dans l'étape
 */
export interface ProgressWaitingMessage {
    /** Délai en ms à partir duquel le message s'affiche */
    afterMs: number;

    /** Texte affiché à l'utilisateur */
    message: string;
}

/**
 * Configuration d'une opération complète
 */
export interface ProgressOperationConfig {
    /** Identifiant unique (ex: 'GDL1', 'BALCPT') */
    id?: string;

    /** Titre affiché dans l'overlay */
    title: string;

    /** Liste des étapes */
    steps: ProgressStep[];

    /** Catégorie pour regroupement  */
    category?: 'report' | 'export' | 'list';

    /** Mode minimal : titre, barre de progression et, s'il y en a un, message d'attente de l'étape */
    minimal?: boolean;

    /** Message secondaire optionnel, affiché sous le titre (ex: avertissement de durée sur gros volume) */
    subtitle?: string;
}

/**
 * État courant de la progression
 */
export interface ProgressState {
    /** Index de l'étape en cours (0-based) */
    currentStepIndex: number;

    /** Progress dans l'étape courante (0-100) */
    currentStepProgress: number;

    /** Progress global (0-100) */
    overallProgress: number;

    /** Statut de l'opération */
    status: ProgressStatus;

    /** Détails optionnels (ex: "Page 5/12") */
    details?: string;

    /** Message d'erreur si status === 'error' */
    errorMessage?: string;
}

export type ProgressStatus = 'idle' | 'running' | 'completed' | 'error';

/**
 * Tracker passé aux opérations pour reporter le progress réel
 */
export interface StepProgressTracker {
    /**
     * Reporte le progress réel de l'étape
     * @param percent Pourcentage (0-100)
     * @param details Détails optionnels (ex: "Page 5/12")
     */
    setProgress(percent: number, details?: string): void;
}

/**
 * Type d'une opération d'étape
 * - Soit un Observable direct
 * - Soit une fonction qui reçoit le tracker et le résultat précédent
 */
export type StepOperation<T = any, P = any> =
    | Observable<T>
    | ((tracker: StepProgressTracker | null, previousResult?: P) => Observable<T>);

/**
 * État d'une étape individuelle (pour l'affichage)
 */
export enum StepState {
    Pending = 'pending',
    Running = 'running',
    Completed = 'completed',
    Error = 'error'
}
